// Signup.jsx 

import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaArrowRightToBracket } from "react-icons/fa6";
import { Icon } from "@iconify/react";
import api from "../../api/axios";
import "../../css/Auth/Signup.css";
import { toast } from "react-toastify";
import AuthCard from "../../components/ui/Cards/AuthCard";
import Input from "../../components/ui/Input/Input";
import Button from "../../components/ui/Button/Button";
import logo from "../../assets/images/logo1.png";
import LogoBottom from "../../assets/images/shreepuram_vijay_prateek.png";
import logo1 from "../../assets/images/logo3.png";
import logo2 from "../../assets/images/Frame 47.png";
import logo3 from "../../assets/images/Frame 35.png";
import logo4 from "../../assets/images/logo7.png";
import logo5 from "../../assets/images/logo4.png";
import { Link } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import Swal from "sweetalert2";

// HELPER: PARSE API RESPONSE

const OTP_EXPIRY_SECONDS = 60;
const OTP_TIME_KEY = "otp_sent_time";

const parseStateDistrictData = (apiResponse) => {
  return apiResponse.state.data.map((item, i) => ({
    stateName: item.state.name,
    districts: item.districts?.[i]?.name?.districts || [],
  }));
};

export const Signup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const passedPhone = location.state?.phoneNumber || "";
  const id = location.state?._id || "";

  // STATE MANAGEMENT

  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isMatch, setIsMatch] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const [rawConsent, setRawConsent] = useState(false);
  
  // State for password validation - track if user has interacted
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");

  const [formData, setFormData] = useState({
    fullName: "",
    phone: passedPhone,
    dob: "",
    password: "",
    confirmPassword: "",
    statePrivate: "",
    districtPrivate: "",
  });

  // Password validation function
  const validatePassword = (pass) => {
    if (!pass) return "";
    if (pass.length < 6) return "Password must be at least 6 characters";
    if (pass.length > 10) return "Password must not exceed 10 characters";
    return "";
  };

  // FETCH STATES

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BASE_URL}/auth/states`)
      .then((res) => res.json())
      .then((data) => {
        setStates(parseStateDistrictData(data));
      })
      .catch((err) => console.log("State API Error:", err));
  }, []);

  // Real-time password validation effect
  useEffect(() => {
    if (passwordTouched) {
      const passError = validatePassword(formData.password);
      setPasswordError(passError);
    }
    
    // Check password match (only if both touched or has values)
    if ((passwordTouched || formData.password) && (confirmPasswordTouched || formData.confirmPassword)) {
      if (formData.password === formData.confirmPassword) {
        if (!passwordError || !formData.password) {
          setIsMatch(true);
          setConfirmPasswordError("");
        } else {
          setIsMatch(false);
          setConfirmPasswordError(passwordError);
        }
      } else if (formData.confirmPassword) {
        setIsMatch(false);
        setConfirmPasswordError("Passwords do not match");
      } else {
        setIsMatch(false);
        setConfirmPasswordError("");
      }
    } else {
      setIsMatch(false);
      setConfirmPasswordError("");
    }
  }, [formData.password, formData.confirmPassword, passwordTouched, confirmPasswordTouched]);

  // INPUT HANDLERS

  const handleChange = (name, value) => {
    // Full Name: only letters and spaces allowed
    if (name === "fullName") {
      const onlyLetters = value.replace(/[^a-zA-Z\s]/g, "");
      setFormData((prev) => ({ ...prev, fullName: onlyLetters }));
      return;
    }
    if (name === "phone") {
      const onlyNums = value.replace(/\D/g, "");
      if (onlyNums.length <= 10) {
        setFormData((prev) => ({ ...prev, phone: onlyNums }));
      }
      return;
    }

    // Password with length limit
    if (name === "password") {
      if (value.length <= 10) {
        setFormData((prev) => ({ ...prev, password: value }));
      }
      if (!passwordTouched && value) {
        setPasswordTouched(true);
      }
      return;
    }
    
    // Confirm password with length limit
    if (name === "confirmPassword") {
      if (value.length <= 10) {
        setFormData((prev) => ({ ...prev, confirmPassword: value }));
      }
      if (!confirmPasswordTouched && value) {
        setConfirmPasswordTouched(true);
      }
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleState = (e) => {
    const selectedState = e.target.value;

    setFormData((prev) => ({
      ...prev,
      statePrivate: selectedState,
      districtPrivate: "",
    }));

    const stateObj = states.find((s) => s.stateName === selectedState);
    setDistricts(stateObj ? stateObj.districts : []);
  };

  const handleDistrict = (e) => {
    setFormData((prev) => ({
      ...prev,
      districtPrivate: e.target.value,
    }));
  };

  // Check if form is valid for submission
  const isFormValid = () => {
    const {
      fullName,
      phone,
      dob,
      password,
      confirmPassword,
      statePrivate,
      districtPrivate,
    } = formData;

    return (
      fullName &&
      phone &&
      dob &&
      password &&
      confirmPassword &&
      statePrivate &&
      districtPrivate &&
      password.length >= 6 &&
      password.length <= 10 &&
      password === confirmPassword &&
      isTermsAccepted
    );
  };

  // SUBMIT HANDLER

  const handleSignup = async () => {
    if (!isTermsAccepted) {
      toast.error("Please accept terms and conditions");
      return;
    }

    const {
      fullName,
      phone,
      dob,
      password,
      confirmPassword,
      statePrivate,
      districtPrivate,
    } = formData;

    if (
      !fullName ||
      !phone ||
      !dob ||
      !password ||
      !confirmPassword ||
      !statePrivate ||
      !districtPrivate
    ) {
      toast.error("All fields are required");
      return;
    }

    // Password length validation
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    if (password.length > 10) {
      toast.error("Password must not exceed 10 characters");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    try {
      setLoading(true);
      const res = await api.post("/auth/send-otp", { phone, dob, parentalConsent: rawConsent });
      toast.success("OTP sent to your phone number");
      if (res.data.message === "OTP sent") {
        localStorage.setItem(OTP_TIME_KEY, Date.now().toString());
        navigate("/otp", { state: { formData, phone } });
      }
    } catch (err) {
      if (err?.response?.data?.parentalConsent) {
        Swal.fire({
          title: "Parental Consent Required",
          text: `You appear to be under 18 years old. A parent or guardian must review and agree to our Terms & Conditions and Privacy Policy on your behalf before you can create an account.

By tapping "I Agree", you confirm that a parent or guardian has consented to your registration.`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "I agree",
          cancelButtonText: "Deny",
          customClass: {
            confirmButton: "swal2-confirm-btn",
            cancelButton: "swal2-cancel-btn"
          },
        }).then((result) => {
          if (result.isConfirmed) {
            setRawConsent(true);
          }
        })
      } else {
        const errorMsg =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Something went wrong";
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthCard
      title="Welcome To Bharat Jagriti ™"
      description="EXPLORE THE TRUTH TO THE WORLD"
      pageTitle="Sign up"
      bottomImg={LogoBottom}
      bottomName="Powered by Sripuram"
      allImg={[logo3, logo2, logo4, logo5, logo1]}
      headLogo={logo}
      msgInput={"Already have an account?"}
      navigateLink={"/login"}
      signUp={" Sign in"}
    >
      <div className="signUp_section px-2 px-md-0">
        {/* Full Name */}
        <Input
          placeholder="Enter your name"
          type="text"
          value={formData.fullName}
          onchangeInput={(e) => handleChange("fullName", e.target.value)}
          disabled={loading}
          inputProps={{ pattern: "[A-Za-z ]*" }}
        />

        {/* Phone and DOB */}
        <div className="number_dob_section">
          <Input
            placeholder="Enter Your Number"
            type="tel"
            value={formData.phone}
            onchangeInput={(e) => handleChange("phone", e.target.value)}
            disabled={loading}
            CountryCode={true}
          />

          <Input
            placeholder="Enter Your DOB"
            type="date"
            value={formData.dob}
            onchangeInput={(e) => handleChange("dob", e.target.value)}
            disabled={loading}
          />
        </div>

        {/* Password hint - only show as hint, not error */}
        <div style={{ marginBottom: "4px" }}>
          <small style={{ 
            fontSize: "11px", 
            color: "#999",
            display: "block"
          }}>
            Password must be 6-10 characters
          </small>
        </div>

        {/* Password Input with Eye Icon */}
        <div style={{ position: "relative", marginBottom: passwordError && passwordTouched ? "2px" : "0px" }}>
          <Input
            placeholder="Enter Password"
            type={showPassword ? "text" : "password"}
            value={formData.password}
            onchangeInput={(e) => handleChange("password", e.target.value)}
            disabled={loading}
            style={{ paddingRight: "40px" }}
          />

          {/* Eye Icon */}
          <span
            onClick={() => setShowPassword((prev) => !prev)}
            style={{
              position: "absolute",
              right: "10px",
              top: "50%",
              transform: "translateY(-50%)",
              cursor: "pointer",
              color: "#666",
              fontSize: "18px",
            }}
          >
            {showPassword ? <FaEye /> : <FaEyeSlash />}
          </span>
        </div>

        {/* Password error message - only show when touched and has error */}
        {passwordError && passwordTouched && (
          <small style={{ 
            color: "red", 
            fontSize: "11px", 
            marginBottom: "8px", 
            display: "block",
            lineHeight: "1.2"
          }}>
            {passwordError}
          </small>
        )}

        {/* Confirm Password with Eye Icon */}
        <div style={{ position: "relative", marginBottom: confirmPasswordError && confirmPasswordTouched ? "2px" : "0px" }}>
          <input
            disabled={loading}
            value={formData.confirmPassword}
            onChange={(e) => handleChange("confirmPassword", e.target.value)}
            type={showConfirmPassword ? "text" : "password"}
            placeholder="Confirm Password"
            style={{ 
              paddingRight: "70px", 
              width: "100%", 
              padding: "12px", 
              borderRadius: "8px", 
              border: "1px solid #ddd",
              fontSize: "14px"
            }}
          />

          {/* Eye Icon for Confirm Password */}
          <span
            onClick={() => setShowConfirmPassword((prev) => !prev)}
            style={{
              position: "absolute",
              right: "40px",
              top: "50%",
              transform: "translateY(-50%)",
              cursor: "pointer",
              color: "#666",
              fontSize: "18px",
            }}
          >
            {showConfirmPassword ? <FaEye /> : <FaEyeSlash />}
          </span>

          {/* Check icon */}
          {formData.password && formData.confirmPassword && isMatch && (
            <Icon
              icon="ic:sharp-check-circle"
              className="text-success"
              style={{
                position: "absolute",
                right: "10px",
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: "20px",
              }}
            />
          )}
        </div>

        {/* Confirm password error message - only show when touched and has error */}
        {confirmPasswordError && confirmPasswordTouched && (
          <small style={{ 
            color: "red", 
            fontSize: "11px", 
            marginBottom: "8px", 
            display: "block",
            lineHeight: "1.2"
          }}>
            {confirmPasswordError}
          </small>
        )}

        {/* Password match success message - compact */}
        {isMatch && formData.password && formData.confirmPassword && 
         formData.password.length >= 6 && formData.password.length <= 10 && (
          <small style={{ 
            color: "green", 
            fontSize: "11px", 
            marginBottom: "8px", 
            display: "block",
            lineHeight: "1.2"
          }}>
            ✓ Passwords match
          </small>
        )}

        {/* STATE & DISTRICT */}
        <div className="dropdown_district" style={{ marginTop: "8px" }}>
          <select
            className="select_state"
            value={formData.statePrivate}
            onChange={handleState}
            style={{ padding: "10px", borderRadius: "8px", border: "1px solid #ddd" }}
          >
            <option value="">State</option>
            {states.map((s, i) => (
              <option key={i} value={s.stateName}>
                {s.stateName}
              </option>
            ))}
          </select>

          <select
            className="select_district"
            value={formData.districtPrivate}
            onChange={handleDistrict}
            disabled={!districts.length}
            style={{ padding: "10px", borderRadius: "8px", border: "1px solid #ddd" }}
          >
            <option value="">District</option>
            {districts.map((d, i) => (
              <option key={i} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Terms & Conditions Checkbox */}
      <div className="form-check d-flex justify-content-center align-items-center mt-3 gap-2 flex-wrap">
        <input
          type="checkbox"
          className="form-check-input"
          id="termsCheck"
          checked={isTermsAccepted}
          onChange={(e) => setIsTermsAccepted(e.target.checked)}
          style={{ width: "16px", height: "16px" }}
        />
        <label
          className="terms_condtion_section form-check-label"
          htmlFor="termsCheck"
          style={{ cursor: "pointer", fontSize: "12px" }}
        >
          I accept{" "}
        </label>
        <Link to="/terms" style={{ fontSize: "12px" }}>terms & conditions</Link> 
        <span style={{ fontSize: "12px" }}>and</span>
        <Link to="/privacy-policy" style={{ fontSize: "12px" }}>privacy policy</Link>
      </div>

      {/* Submit Button */}
      <Button
        onClick={handleSignup}
        className="NextBtn"
        style={{
          width: "70%",
          color: "white",
          padding: "10px 20px",
          borderRadius: "25px",
          backgroundColor: "var(--color1)",
          margin: "20px auto 10px auto",
          display: "flex",
          justifyContent: "center",
          fontSize: "14px",
          alignItems: "center",
          opacity: isFormValid() ? 1 : 0.6,
          cursor: isFormValid() ? "pointer" : "not-allowed",
          border: "none",
        }}
        disabled={!isFormValid() || loading}
        children={loading ? "Submitting..." : "Submit"}
        icon={FaArrowRightToBracket}
      />
    </AuthCard>
  );
};

export default Signup;